import { easeLinear, select } from 'd3';
import { BAR_GRAPH_DEFAULT_STACKED_COLORS } from '../constants/barGraph';
import {
  BarVariant,
  ChartDimensions,
  IBarPoint,
  IBarPoints,
  IStackedBarPoint,
  IStackedBarPoints,
  XScale,
  YScale
} from '../interfaces/barGraph';


let nextUniqueId = 0;

export class BarGraph {
  private readonly _uniqueId: string;

  constructor(
    private svg: any,
    private data: IBarPoints,
    private xScale: XScale,
    private yScale: YScale,
    private chartDimensions: ChartDimensions,
    private yMin: number,
    private barSpacing: number,
    private barVariant: BarVariant,
    private barBackdrop: boolean,
    private stackedVariantColors = BAR_GRAPH_DEFAULT_STACKED_COLORS
  ) {
    this._uniqueId = `bar-${ nextUniqueId++ }`;
  }

  drawBarGraph() {
    const barWidth = this._getBarWidth();

    if (this.barBackdrop) {
      this.addBackdrop();
    }

    this.svg.selectAll('.bar_line')
      .data(this.data)
      .enter()
      .append('rect')
      .attr('x', (d: any) => this.xScale(d.name))
      .attr('y', this.yScale(this.yMin))
      .attr('rx', (d: any) => {
        return this._getRx(d, barWidth);
      })
      .attr('ry', (d: any) => {
        return this._getRy(d, barWidth);
      })
      .attr('width', barWidth)
      .attr('height', 1)
      .attr('id', 'bar-graph')
      .transition()
      .attr('y', (d: any) => {
        return this._getY(d);
      })
      .attr('height', (d: any) => {
        return this._getHeight(d);
      })
      .attr('class', () => {
        return this._getBarClasses();
      })
      .attr('fill', (_d: any, index: number) => {
        return this._getFill(index);
      })
      .attr('clip-path', (_d: any, index: number) => {
        if (!this.barBackdrop) {
          return null;
        }

        return `url(#backdrop_${ index })`;
      });

    this._createLinearGradient();
  }

  updateBarGraph(currentData, xScale, yScale, yMin, chartDimensions, barSpacing, barVariant, stackedVariantColors?: string[]) {
    this.data = currentData;
    this.xScale = xScale;
    this.yScale = yScale;
    this.yMin = yMin;
    this.chartDimensions = chartDimensions;
    this.barSpacing = barSpacing;
    this.barVariant = barVariant;

    if (stackedVariantColors) {
      this.stackedVariantColors = stackedVariantColors;
    }

    const barWidth = this._getBarWidth();

    this.svg.selectAll('.bar_line').interrupt();

    if (this.barBackdrop) {
      this.addBackdrop();
    }

    this.svg.selectAll('.bar_line')
      .data(this.data)
      .transition()
      .duration(300)
      .ease(easeLinear)
      .attr('x', (d: any) => this.xScale(d.name))
      .attr('y', (d: any) => {
        return this._getY(d);
      })
      .attr('rx', (d: any) => {
        return this._getRx(d, barWidth);
      })
      .attr('ry', (d: any) => {
        return this._getRy(d, barWidth);
      })
      .attr('width', barWidth)
      .attr('height', (d: any) => {
        return this._getHeight(d);
      })
      .attr('class', () => {
        return this._getBarClasses();
      })
      .attr('fill', (_d: any, index: number) => {
        return this._getFill(index);
      })
      .attr('clip-path', (_d: any, index: number) => {
        if (!this.barBackdrop) {
          return null;
        }

        return `url(#backdrop_${ index })`;
      });

    this._createLinearGradient();
  }

  private _createLinearGradient() {
    if (this.barVariant === BarVariant.STACKED) {
      this.addLinearGradientForStackedData();
    }

    if (this.barVariant === BarVariant.GRADIENT) {
      this.addLinearGradient();
    }
  }

  addBackdrop() {
    const barWidth = this._getBarWidth();

    this.svg.selectAll('.bar-line-backdrop').remove();
    this.svg.selectAll('.bar-line-backdrop-clip-path').remove();

    this.svg.selectAll('.bar-line-backdrop')
      .data(this.data)
      .enter()
      .append('rect')
      .attr('x', (d: any) => this.xScale(d.name))
      .attr('y', 0)
      .attr('rx', barWidth / 2)
      .attr('ry', barWidth / 2)
      .attr('width', barWidth)
      .attr('height', this.chartDimensions.chartHeight)
      .attr('class', 'bar-line-backdrop');

    this.svg.selectAll('.bar-line-backdrop-clip-path')
      .data(this.data)
    .enter()
    .append('clipPath')
    .attr('class', 'bar-line-backdrop-clip-path')
    .attr('id', (_d: IBarPoint, index: number) => `backdrop_${ index }`)
    .append('rect')
    .attr('x', (d: any) => this.xScale(d.name))
    .attr('y', 0)
    .attr('rx', barWidth / 2)
    .attr('ry', barWidth / 2)
    .attr('width', barWidth)
    .attr('height', this.chartDimensions.chartHeight);
  }

  addLinearGradient() {
    this.svg.selectAll('linearGradient').remove();
    const selection = this.svg.selectAll('linearGradient')
      .data(this.data);

    selection.enter()
      .append('linearGradient')
      .attr('id', (_d: IBarPoint, index: number) => `mainGradient${ index }`)
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '0%')
      .attr('y2', '100%');

    this.svg.selectAll('linearGradient[id^="mainGradient"]')
      .append('stop')
      .attr('class', 'stop-top')
      .attr('offset', (d: IBarPoint) => {
        if (!d.extra.pointAnomalous || d.extra.areaMax === 0) {
          return '0%';
        }
        return `${ (d.value - d.extra.areaMax) / (d.value) * 100 }%`;
      });

    this.svg.selectAll('linearGradient[id^="mainGradient"]')
      .append('stop')
      .attr('class', 'stop-bottom')
      .attr('offset', (d: IBarPoint) => {
        if (!d.extra.pointAnomalous || d.extra.areaMax === 0) {
          return '0%';
        }
        return `${ ((d.value - d.extra.areaMax) / (d.value) * 100) + 15 }%`;
      });
  }

  addLinearGradientForStackedData() {
    this.svg.selectAll('linearGradient').remove();

    const [ topColor, bottomColor ] = this.stackedVariantColors;
    const selection = this.svg.selectAll('linearGradient').data(this.data);

    selection.enter()
      .append('linearGradient')
      .attr('id', (_d: IStackedBarPoints, index: number) => `eventGradient-${ this._uniqueId }-${ index }`)
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '0%')
      .attr('y2', '100%');

    const self = this;

    this.svg.selectAll('linearGradient').each(function(d: IStackedBarPoint) {
      const topPercent = Math.ceil(d.stackedData[0] / (d.value) * 100);
      const bottomPercent = Math.ceil(d.stackedData[1] / (d.value) * 100);

      /**
       * Set a min height of 2px for each step, if their value is greater than 0, so that they are clearly visible.
       * Set gutter size as 1px.
       */
      const barHeight = self._getHeight(d);
      const minStepPercent = Math.ceil(2 / barHeight * 100);
      const gutterPercent = Math.ceil(1 / barHeight * 100);

      let topPercentDisplay = Math.max(minStepPercent, topPercent);
      /**
       * Restrict top step to max 100% - minStepPercent - gutterPercent
       * to allow enough space for gutter and bottom step.
       */
      if (bottomPercent) {
        topPercentDisplay = Math.min(100 - minStepPercent - gutterPercent, topPercentDisplay);
      }

      let bottomOffset = topPercentDisplay;

      if (topPercent) {
        select(this)
          .append('stop')
          .attr('class', `stop-stacked-${ topColor }`)
          .attr('offset', `${ topPercentDisplay }%`);
      }

      if (topPercent && bottomPercent) {
        bottomOffset += gutterPercent;

        select(this)
          .append('stop')
          .attr('class', 'stop-stacked-gutter')
          .attr('offset', `${ topPercentDisplay }%`);

        select(this)
          .append('stop')
          .attr('class', 'stop-stacked-gutter')
          .attr('offset', `${ topPercentDisplay + gutterPercent }%`);
      }

      if (bottomOffset) {
        select(this)
          .append('stop')
          .attr('class', `stop-stacked-${ bottomColor }`)
          .attr('offset', `${ bottomOffset }%`);
      }
    });

    this.svg.selectAll('linearGradient')
      .append('stop')
      .attr('class', `stop-stacked-${ bottomColor }`)
      .attr('offset', '100%');
  }

  fadeBarsOnHover(point) {
    this.svg.selectAll('#bar-graph').style('opacity', (_data: IBarPoint) => point.name === _data.name ? 1 : .5);
  }

  resetBarsOpacity() {
    this.svg.selectAll('#bar-graph').style('opacity', 1);
  }

  private _getBarWidth() {
    const d0 = this.data[0];
    const d1 = this.data[1];

    let barWidth = Math.floor(this.xScale(d1.name) - this.xScale(d0.name) - this.barSpacing);
    if (!barWidth) {
      barWidth = 5;
    }

    return barWidth;
  }

  private _getY(d: any) {
    if (d.value !== 0) {
      return this.yScale(d.value) >= this.yScale(this.yMin) - 1 ? this.yScale(this.yMin) - 1 : this.yScale(d.value);
    } else {
      return this.yScale(this.yMin) - 1;
    }
  }

  private _getRx(d: any, barWidth: number) {
    if (this.barBackdrop) {
      return 0;
    }

    if (d.value !== 0 && (this.chartDimensions.chartHeight - this.yScale(d.value) >= 1)) {
      return barWidth / 2;
    } else {
      return '0';
    }
  }

  private _getRy(d: any, barWidth: number) {
    if (this.barBackdrop) {
      return 0;
    }

    if (d.value !== 0) {
      return barWidth / 2;
    } else {
      return '0';
    }
  }

  private _getHeight(d: any) {
    let height: number;
    height = this.chartDimensions.chartHeight - this.yScale(d.value);
    // min height is always 1
    return height < 1 && !this.barBackdrop ? 1 + height : height;
  }

  private _getBarClasses() {
    if (this.barVariant === BarVariant.GRADIENT || this.barVariant === BarVariant.STACKED) {
      return 'bar_line';
    }

    return 'bar_line event_bar';
  }

  private _getFill(index) {
    if (this.barVariant === BarVariant.GRADIENT) {
      return `url(#mainGradient${ index })`;
    }

    if (this.barVariant === BarVariant.STACKED) {
      return `url(#eventGradient-${ this._uniqueId }-${ index })`;
    }
  }
}
